import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Sparkles, Pencil, RefreshCw } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";

const inputCls = "w-full rounded-lg bg-white border border-[#120716]/15 px-4 py-3 text-sm text-[#120716] focus:outline-none focus:border-[#E84C4C] transition-colors";

export const ReflectionCard = ({ verseRef }) => {
  const [reflection, setReflection] = useState(null);
  const [state, setState] = useState("loading");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setState("loading");
    try {
      const { data } = await api.get("/verses/today/reflection");
      setReflection(data);
      setState("ready");
    } catch (e) {
      setState("error");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleError = (err) => toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);

  const regenerate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/verses/today/reflection/regenerate");
      setReflection(data);
      setState("ready");
      setEditing(false);
      toast.success("Claude wrote a fresh reflection");
    } catch (err) { handleError(err); } finally { setBusy(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/verses/today/reflection", { text: draft });
      setReflection(data);
      setState("ready");
      setEditing(false);
      toast.success("Reflection saved");
    } catch (err) { handleError(err); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl bg-white border border-[#120716]/10 shadow-sm p-6 lg:col-span-2" data-testid="reflection-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-orbitron text-sm tracking-widest text-[#120716]/70 flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#E84C4C]" /> TODAY'S REFLECTION</div>
          <p className="text-xs text-[#120716]/50 mt-1">
            Written by Claude for {verseRef || "today's verse"} · {reflection?.generated_by === "luke" ? "edited by you" : "auto-generated"}. Shown under the Verse of the Day on the homepage.
          </p>
        </div>
        <div className="flex gap-2">
          <button data-testid="reflection-edit-btn" disabled={busy || state !== "ready"} onClick={() => { setDraft(reflection?.text || ""); setEditing(true); }} className="inline-flex items-center gap-2 rounded-full border border-[#120716]/15 text-[#120716]/70 hover:text-[#120716] disabled:opacity-40 text-xs font-semibold px-4 py-2 transition-colors"><Pencil className="w-3.5 h-3.5" /> Edit</button>
          <button data-testid="reflection-regenerate-btn" disabled={busy} onClick={regenerate} className="inline-flex items-center gap-2 rounded-full bg-[#E84C4C] hover:bg-[#f05e5e] disabled:opacity-50 text-white text-xs font-semibold px-4 py-2 transition-all"><RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> {busy ? "Working..." : "Regenerate"}</button>
        </div>
      </div>
      <div className="mt-5">
        {editing ? (
          <form onSubmit={save} className="space-y-3">
            <textarea data-testid="reflection-textarea" rows={6} required minLength={10} className={inputCls} value={draft} onChange={(e) => setDraft(e.target.value)} />
            <div className="flex gap-3">
              <button type="submit" disabled={busy} data-testid="reflection-save-btn" className="rounded-full bg-[#E84C4C] hover:bg-[#f05e5e] disabled:opacity-50 text-white text-sm font-semibold px-6 py-2.5 transition-all">{busy ? "Saving..." : "Save reflection"}</button>
              <button type="button" onClick={() => setEditing(false)} data-testid="reflection-cancel-btn" className="rounded-full border border-[#120716]/15 text-[#120716]/70 text-sm font-semibold px-5 py-2.5">Cancel</button>
            </div>
          </form>
        ) : state === "loading" ? (
          <p className="text-sm text-[#120716]/45 italic" data-testid="reflection-loading">Loading today's reflection...</p>
        ) : state === "error" ? (
          <p className="text-sm text-[#E84C4C]" data-testid="reflection-error">Couldn't load the reflection. Try Regenerate.</p>
        ) : (
          <p className="text-sm text-[#120716]/75 leading-relaxed whitespace-pre-line" data-testid="reflection-text">{reflection.text}</p>
        )}
      </div>
    </div>
  );
};
